// Contratos del rol PANTALLA (kiosco TV de planta) y de su configuración por
// el coordinador (C15). Spec: docs/spec-mvp/roles/07-pantalla.md.
//
// El rol `pantalla` es SOLO LECTURA por construcción: únicamente está
// autorizado en /api/pantalla/* (todos GET). Su configuración la edita el
// coordinador vía /api/coordinador/configuracion/pantalla.
import { z } from 'zod';
import { conectividadLecturaSchema } from './ingesta_maquina.js';
/** Paneles disponibles en la rotación de la TV. El orden del array de config es el orden de rotación. */
export const panelPantallaSchema = z.enum([
    'lideres', // Ranking de líderes del turno
    'podio', // Top 3 líderes
    'supervisores', // Ranking de supervisores del día
    'lineas', // Grid de líneas de producción
    'turno', // Resumen del turno
    'pits', // Entrada a Pits: min/evento por causal configurable vs meta
]);
// Nota (2026-09, panel pits): el jsonb `paneles` de config_pantalla es
// extensible sin migración. Una planta YA configurada conserva su array
// guardado → el panel nuevo no aparece hasta que el coordinador lo active en
// C15 (comportamiento deseado). Plantas sin fila usan este default completo.
export const PANELES_PANTALLA_DEFAULT = [
    'lideres',
    'podio',
    'supervisores',
    'lineas',
    'turno',
    'pits',
];
/**
 * Base del cumplimiento (decisión 2026-08-20, dual):
 * - `capacidad`: producido / (capacidad estándar × horas capturadas) — por turno.
 * - `programa`: producido / programa vigente del cliente — diario (el programa
 *   no tiene dimensión turno; se muestra como avance del día, igual que C1).
 */
export const baseCumplimientoSchema = z.enum(['capacidad', 'programa']);
/**
 * Tema visual de la TV (pedido UPG 2026-08-25): en planta no se sabe cuál se
 * lee mejor con la luz ambiente, así que lo elige el coordinador en C15.
 * Default `oscuro` (el comportamiento histórico de marca).
 */
export const temaPantallaSchema = z.enum(['oscuro', 'claro']);
/** Configuración de la pantalla de una planta (fila de `config_pantalla`, o defaults si no hay fila). */
export const configPantallaSchema = z.object({
    planta_id: z.string().uuid(),
    paneles: z.array(panelPantallaSchema).min(1),
    segundos_rotacion: z.number().int().min(10).max(300),
    /** Si está seteado, la TV no rota: muestra solo ese panel. */
    panel_fijo: panelPantallaSchema.nullable(),
    base_cumplimiento: baseCumplimientoSchema,
    andon_habilitado: z.boolean(),
    /** Minutos de paro abierto que disparan el modo andon. */
    andon_min_paro: z.number().int().min(5).max(120),
    tema: temaPantallaSchema,
    /** null = la planta aún no tiene fila guardada (defaults). */
    actualizado_at: z.string().nullable(),
});
/** Body del PUT de configuración (coordinador). */
export const configPantallaUpdateSchema = z
    .object({
    planta_id: z.string().uuid(),
    paneles: z
        .array(panelPantallaSchema)
        .min(1)
        .refine((p) => new Set(p).size === p.length, {
        message: 'paneles no puede tener claves repetidas',
    }),
    segundos_rotacion: z.number().int().min(10).max(300),
    panel_fijo: panelPantallaSchema.nullable(),
    base_cumplimiento: baseCumplimientoSchema,
    andon_habilitado: z.boolean(),
    andon_min_paro: z.number().int().min(5).max(120),
    /** Default en el contrato: un cliente viejo (pestaña abierta pre-deploy) guarda sin romper. */
    tema: temaPantallaSchema.default('oscuro'),
})
    .refine((c) => c.panel_fijo === null || c.paneles.includes(c.panel_fijo), {
    message: 'panel_fijo debe estar entre los paneles activos',
});
/** Respuesta del GET de configuración del coordinador (C15). */
export const configPantallaCoordinadorSchema = z.object({
    plantas: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), codigo: z.string() })),
    config: configPantallaSchema,
});
// ───────────────────────────── Datos en vivo ─────────────────────────────
/**
 * Minutos perdidos por tipo (B11, propuesta 2026-10-alimentos-rionegro). Se
 * clasifica por la categoría de la causal del paro, en este orden:
 *   averia = categoría `es_mantenimiento`; cambio = `es_cambio_referencia`;
 *   espera = código `ES` o nombre que empieza por «Espera»; otros = el resto.
 * Incluye todas las categorías (también las que no afectan disponibilidad,
 * como el cambio de referencia) y los paros abiertos contados hasta ahora.
 */
export const minutosParoPorTipoSchema = z.object({
    averia: z.number().min(0),
    espera: z.number().min(0),
    cambio: z.number().min(0),
    otros: z.number().min(0),
    /**
     * Minutos con la causal pendiente de ingesta (`ingesta_config.causal_pendiente_id`,
     * p. ej. SC-MAQ): se separan de la categoría de su madre para rotularlos
     * «Sin clasificar», como el supervisor y la tablet. La suma total no cambia.
     * Opcional por compatibilidad con respuestas anteriores.
     */
    sin_clasificar: z.number().min(0).optional(),
});
export const pantallaLineaSchema = z.object({
    linea_id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    /** Líder programado para (día operativo, turno actual). null si no hay programación. */
    lider_nombre: z.string().nullable(),
    producido: z.number().min(0),
    /** Meta del turno (capacidad × horas capturadas). Las líneas siempre miden vs meta del turno. */
    esperado: z.number().min(0),
    cumplimiento_pct: z.number().min(0).nullable(),
    minutos_paro: z.number().min(0),
    estado: z.enum(['verde', 'amarillo', 'rojo', 'sin_actividad']),
    /** Ingesta de máquina (opcional): null en líneas manuales. */
    ...conectividadLecturaSchema.shape,
    /**
     * B11 (opcional): minutos perdidos del turno en curso por tipo. Ausente si la
     * planta no tiene líneas de máquina o el tenant no marca categorías.
     */
    minutos_paro_por_tipo: minutosParoPorTipoSchema.nullable().optional(),
    /**
     * B11 (opcional): kilos regalados del día operativo (neto, con signo; mismo
     * cálculo que el sobrellenado del coordinador). null si el SKU no declara
     * peso nominal o las horas no traen kilos.
     */
    sobrellenado_kg_hoy: z.number().nullable().optional(),
    /** B11 (opcional): gramos de más por unidad, promedio ponderado del día. */
    sobrellenado_g_unidad_hoy: z.number().nullable().optional(),
    /**
     * R12 (opcional): true si la línea está conectada a máquina (híbrida o
     * máquina) y sus cifras se calculan en vivo sobre los registros, sin esperar
     * el refresco de la vista materializada. Ausente en líneas manuales.
     */
    en_vivo: z.literal(true).optional(),
});
export const pantallaLiderSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    area_nombre: z.string().nullable(),
    linea_codigo: z.string().nullable(),
    producido: z.number().min(0),
    /** Denominador según base_cumplimiento de la config. 0 = sin base comparable. */
    esperado: z.number().min(0),
    cumplimiento_pct: z.number().min(0).nullable(),
});
export const pantallaSupervisorSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    producido: z.number().min(0),
    /** Siempre vs capacidad (no existe programa por turno). */
    esperado: z.number().min(0),
    cumplimiento_pct: z.number().min(0).nullable(),
    validaciones: z.number().int().min(0),
    invalidaciones: z.number().int().min(0),
    lideres: z.number().int().min(0),
});
// ── Panel Entrada a Pits (pedido Robotpack 2026-09) ──────────────────────
//
// Promedio de minutos por EVENTO de paro para las causales participantes del
// indicador (filas default de `metas_entrada_pits`, ej. FO01/FO07), acotado
// al DÍA OPERATIVO completo (no al turno en curso — mismo criterio que el
// ranking de supervisores). Fuente: registros_paro DIRECTO (no MVs).
export const pantallaPitsLiderSchema = z.object({
    user_id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
    /** Promedio min/evento del líder hoy (solo líderes con eventos). */
    promedio_minutos: z.number().min(0),
    eventos_count: z.number().int().min(1),
});
export const pantallaPitsCausalSchema = z.object({
    causal_id: z.string().uuid(),
    causal_nombre: z.string(),
    /** Meta default de la causal (min/evento) — la TV no resuelve overrides por línea. */
    meta_minutos: z.number().positive(),
    /** Promedio global del día. Null si aún no hay eventos hoy. */
    promedio_minutos: z.number().min(0).nullable(),
    eventos_count: z.number().int().min(0),
    /** Líderes CON eventos hoy, ordenados por promedio ASC (mejor primero). */
    lideres: z.array(pantallaPitsLiderSchema),
});
export const pantallaParoAbiertoSchema = z.object({
    paro_id: z.string().uuid(),
    linea_codigo: z.string(),
    linea_nombre: z.string(),
    lider_nombre: z.string().nullable(),
    causal: z.string().nullable(),
    /** Minutos corriendo, medidos en fake-UTC contra el reloj del tenant. */
    minutos_abierto: z.number().int().min(0),
});
export const pantallaDatosSchema = z.object({
    tenant_nombre: z.string(),
    planta: z.object({ id: z.string().uuid(), nombre: z.string(), codigo: z.string() }),
    fecha_operativa: z.string(),
    /** Reloj del tenant al momento de la respuesta (ISO local sin zona, ej. "2026-08-20T09:58:17"). */
    hora_tenant: z.string(),
    turno: z
        .object({
        id: z.string().uuid(),
        numero: z.number().int(),
        nombre: z.string(),
        hora_inicio: z.string(),
        hora_fin: z.string(),
        /** % transcurrido del turno al momento de la respuesta. */
        pct_transcurrido: z.number().min(0).max(100),
    })
        .nullable(),
    config: configPantallaSchema,
    resumen: z.object({
        producido: z.number().min(0),
        esperado: z.number().min(0),
        cumplimiento_pct: z.number().min(0).nullable(),
        lineas_activas: z.number().int().min(0),
        lineas_en_meta: z.number().int().min(0),
        lineas_riesgo: z.number().int().min(0),
        minutos_paro: z.number().min(0),
        faltante: z.number().min(0),
    }),
    /** B11 (opcional): totales de la planta. Ausente bajo las mismas condiciones que en la línea. */
    resumen_hoy: z
        .object({
        minutos_paro_por_tipo: minutosParoPorTipoSchema,
        sobrellenado_kg: z.number().nullable(),
    })
        .nullable()
        .optional(),
    lineas: z.array(pantallaLineaSchema),
    lideres: z.array(pantallaLiderSchema),
    supervisores: z.array(pantallaSupervisorSchema),
    paros_abiertos: z.array(pantallaParoAbiertoSchema),
    /** Panel pits: una entrada por causal participante. [] si el tenant no tiene metas. */
    pits: z.array(pantallaPitsCausalSchema),
    /**
     * R12 (opcional): true si la planta tiene al menos una línea conectada a
     * máquina; la TV consulta entonces cada 10 s en lugar de cada 30 s.
     */
    tiene_lineas_maquina: z.boolean().optional(),
});
/** Query del GET /api/pantalla/datos. planta_id solo aplica a coordinador/directivo/super_admin (vista previa). */
export const pantallaDatosQuerySchema = z.object({
    planta_id: z.string().uuid().optional(),
});
//# sourceMappingURL=pantalla.js.map